import { React, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { BlockStack, Text, InlineStack } from "@shopify/polaris";
import "leaflet/dist/leaflet.css";
import { geocodeLocation } from "../../utility/mexicoGeo";

// Fix Leaflet's default marker asset paths (same approach as CarMap.js).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const MEXICO_CENTER = [23.6345, -102.5528];

// A round pin showing how many sucursales sit at this point. Green = precise
// stored coordinates, blue = approximate (city/state) fallback.
const countIcon = (count, precise) =>
  L.divIcon({
    className: "provider-count-marker",
    html: `<div style="
      display:flex;align-items:center;justify-content:center;
      width:28px;height:28px;border-radius:50%;
      background:${precise ? "#1f8f5f" : "#2c6ecb"};color:#fff;
      font-size:13px;font-weight:600;line-height:1;
      border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);
    ">${count}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14],
  });

// Group locations that resolve to the same coordinate so we render one marker
// per city instead of stacking many pins on the exact same point.
function groupByCoord(locations) {
  const groups = new Map();
  let unresolved = 0;

  locations.forEach((loc) => {
    // Prefer precise stored coordinates; fall back to city-level geocoding.
    const lat = Number(loc.latitude);
    const lng = Number(loc.longitude);
    const hasPrecise =
      loc.latitude != null &&
      loc.longitude != null &&
      Number.isFinite(lat) &&
      Number.isFinite(lng);

    const geo = hasPrecise
      ? { lat, lng, precision: "exact" }
      : geocodeLocation({ city: loc.city, state: loc.state });
    if (!geo) {
      unresolved += 1;
      return;
    }
    // Precise pins keep full resolution so distinct sucursales don't merge;
    // city-fallback pins round to ~100 m so same-city rows share one marker.
    const round = geo.precision === "exact" ? 5 : 3;
    const key = `${geo.lat.toFixed(round)},${geo.lng.toFixed(round)}`;
    if (!groups.has(key)) {
      groups.set(key, { lat: geo.lat, lng: geo.lng, items: [] });
    }
    groups.get(key).items.push({ ...loc, precision: geo.precision });
  });

  return { groups: Array.from(groups.values()), unresolved };
}

function ProviderMap({ locations }) {
  const { groups, unresolved } = useMemo(
    () => groupByCoord(locations || []),
    [locations],
  );

  const plotted = groups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <BlockStack gap="200">
      <InlineStack gap="400" blockAlign="center">
        <Text tone="subdued">
          {plotted} sucursal{plotted === 1 ? "" : "es"} en el mapa
          {unresolved > 0
            ? ` · ${unresolved} sin ubicación`
            : ""}
        </Text>
        <InlineStack gap="300" blockAlign="center">
          <InlineStack gap="100" blockAlign="center">
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: "#1f8f5f",
                display: "inline-block",
              }}
            />
            <Text tone="subdued" variant="bodySm">
              Coordenadas precisas
            </Text>
          </InlineStack>
          <InlineStack gap="100" blockAlign="center">
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: "#2c6ecb",
                display: "inline-block",
              }}
            />
            <Text tone="subdued" variant="bodySm">
              Aproximada (por ciudad)
            </Text>
          </InlineStack>
        </InlineStack>
      </InlineStack>
      <div
        style={{
          width: "100%",
          height: "70vh",
          borderRadius: "8px",
          overflow: "hidden",
          border: "1px solid var(--p-color-border, #e1e3e5)",
        }}
      >
        <MapContainer
          center={MEXICO_CENTER}
          zoom={5}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap'
          />
          {groups.map((group) => (
            <Marker
              key={`${group.lat},${group.lng}`}
              position={[group.lat, group.lng]}
              icon={countIcon(
                group.items.length,
                group.items[0]?.precision === "exact"
              )}
            >
              <Popup>
                <div style={{ maxHeight: 220, overflowY: "auto", minWidth: 200 }}>
                  <BlockStack gap="200">
                    {group.items.map((loc) => (
                      <div key={loc.id}>
                        <Text variant="bodySm" fontWeight="semibold">
                          {loc.provider_name}
                        </Text>
                        <div>
                          <Text variant="bodySm" tone="subdued">
                            {loc.label || "Sucursal"}
                            {loc.precision !== "exact" ? " · ubicación aprox." : ""}
                          </Text>
                        </div>
                        <Text variant="bodySm">
                          {[loc.street_1, loc.street_2, loc.city, loc.state]
                            .map((x) => (x || "").trim())
                            .filter(Boolean)
                            .join(", ")}
                        </Text>
                      </div>
                    ))}
                  </BlockStack>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </BlockStack>
  );
}

export default ProviderMap;
